import { AnalyticsService } from '../..'
import { Entity } from '../../../../domain/entity'
import { IEvent } from '../../../../domain/event/event'
import { DOMEvents, DOMUtils } from '../../../../utils/dom'
import { IProfile } from '../../../common/profile'
import { IAnalyticsAdapter, IAnalyticsProviderConfig } from '../adapter'

import { Button } from '../../../../domain/analytics/providers/mparticle/events/click/button'
import { Consent } from '../../../../domain/analytics/providers/mparticle/events/click/consent'
import { InternalPromo } from '../../../../domain/analytics/providers/mparticle/events/click/internal-promo'
import { Navigation } from '../../../../domain/analytics/providers/mparticle/events/click/navigation'
import { PhotoGallery } from '../../../../domain/analytics/providers/mparticle/events/click/photo-gallery'
import { SocialShare } from '../../../../domain/analytics/providers/mparticle/events/click/social-share'
import { SubscribeNewsletter } from '../../../../domain/analytics/providers/mparticle/events/click/subscribe-newsletter'
import { Widget } from '../../../../domain/analytics/providers/mparticle/events/click/widget'
import { Default } from '../../../../domain/analytics/providers/mparticle/events/default'
import { IMparticleEvent } from '../../../../domain/analytics/providers/mparticle/events/i-mparticle-event'
import { MparticleEvent } from '../../../../domain/analytics/providers/mparticle/events/mparticle-event'

import { ArticleView } from '../../../../domain/analytics/providers/mparticle/events/pageload/article-view'
import { CategoryView } from '../../../../domain/analytics/providers/mparticle/events/pageload/category-view'
import { PageError } from '../../../../domain/analytics/providers/mparticle/events/pageload/page-error'
import { ScreenView } from '../../../../domain/analytics/providers/mparticle/events/pageload/screen-view'
import { Search } from '../../../../domain/analytics/providers/mparticle/events/pageload/search'
import { VideoCenter } from '../../../../domain/analytics/providers/mparticle/events/pageload/video-center'
import { WidgetVideo } from '../../../../domain/analytics/providers/mparticle/events/pageload/widget-video'

import { PageScroll } from '../../../../domain/analytics/providers/mparticle/events/scroll/page-scroll'
import { WidgetInview } from '../../../../domain/analytics/providers/mparticle/events/scroll/widget-inview'

import { Subscription } from '../../../../domain/analytics/providers/mparticle/events/user/subscription'

import { Campaign } from '../../../../domain/analytics/providers/mparticle/events/pageload/campaign'
import { CustomSubscription } from '../../../../domain/analytics/providers/mparticle/events/user/custom-subscription'
import { SetAttributes } from '../../../../domain/analytics/providers/mparticle/events/user/set-attributes'
import { ViafouraEvents } from '../../../../domain/analytics/providers/mparticle/events/viafoura/viafoura-events'
import { VideoEvents } from '../../../../domain/analytics/providers/mparticle/events/video/video-events'

import { Debug } from '../../../../utils/debug'

export interface IWindow extends Window {
    mParticle: any
}

declare let window: IWindow

export class MParticle extends Entity implements IAnalyticsAdapter<IAnalyticsProviderConfig> {
    public events: IEvent[]
    public signIn: boolean
    public signOut: boolean

    /**
     * initialize mParticle
     *
     * TODO: isDevelopmentMode from configuration object
     * isDevelopmentMode: false (or removed) fro production
     *
     * identifyRequest: IDSync request, contains desired indentify
     * request. mParticle will use recent/previous indentities if
     * not provided
     * https://docs.mparticle.com/developers/sdk/web/idsync/
     *
     * identityCallback: get executed on successfull completion of
     * IDSync. Provides mParticle init information
     */
    public initialize(
        creds: IAnalyticsProviderConfig,
        profile: IProfile,
        events: IMparticleEvent[],
        meta: object): void {

        const identityRequest = {
            userIdentities: {
                customerid: profile.uuid ? String(profile.uuid) : '',
                email: profile.email ? String(profile.email) : ''
            }
        }

        const config = {
            appVersion: '1.0.0',
            customFlags: {},
            identifyRequest: identityRequest,
            identityCallback: this.mParticleCallback.bind(this, profile, meta),
            isDebug: true,
            isDevelopmentMode: creds.isDev,
            isSandbox: true,
            sessionTimeout: 30,
            useCookieStorage: true,
            useNativeSdk: false,
        }

        // setup global object and queue
        window.mParticle = window.mParticle || { config: { rq: [] } }
        window.mParticle.config = { ...window.mParticle.config, ...config }
        window.mParticle.ready = (f: () => void) => window.mParticle.config.rq.push(f)

        // load JS
        DOMUtils.loadScript(`//jssdkcdns.mparticle.com/js/v2/${creds.account}/mparticle.js`)

        // bind events
        this.bindEvents(events, meta)

        // bind video events
        this.bindVideoEvents(meta)

        // bind viafoura events
        this.bindViafouraEvents(meta)

        // bind subscription event
        this.bindSubscriptionEvents(meta)
    }

    /**
     * mParticle init callback
     */
    public mParticleCallback(profile: IProfile, meta: any, result: any) {
        const currentIdentities: any = result.getUser().getUserIdentities().userIdentities
        if (
            currentIdentities.other2 !== localStorage.getItem('globalmParticleCookieID')
            || null !== JSON.parse(localStorage.getItem('janrainCaptureProfileData'))
        ) {
            const identityObject: any = {}
            identityObject.userIdentities = {}
            if (currentIdentities.other2 !== localStorage.getItem('globalmParticleCookieID')) {
                identityObject.userIdentities.other2 = String(localStorage.getItem('globalmParticleCookieID'))
            }
            if (null !== JSON.parse(localStorage.getItem('janrainCaptureProfileData'))) {
                identityObject.userIdentities.customerid =
                    JSON.parse(localStorage.getItem('janrainCaptureProfileData')).uuid
                identityObject.userIdentities.email =
                    JSON.parse(localStorage.getItem('janrainCaptureProfileData')).email
            }
            window.mParticle.Identity.identify(identityObject, () => {
                // still need to handle the user events
                SetAttributes(result, profile, meta)
            })
        }

        /**
         * Implement mParticle debug for QA should append
         * mParticle Id and device Id to be body
         */
        const debug = new Debug()
        debug.display()
    }

    /**
     * User change events
     */
    public identityChange(profile: IProfile) {
        // Set user attributes
        SetAttributes({}, profile, {})
    }

    /**
     * Get the correct event object for the registered event type
     *
     * @param type
     * @param value
     * @param action
     * @param element
     */
    public getEventByType(type: string, value: string, action: string, element?: Element): IEvent {
        if (action === DOMEvents.click) {
            switch (type.toLowerCase()) {
                case AnalyticsService.clickEventTypes.button:
                    return new Button(type, value, action, element)
                case AnalyticsService.clickEventTypes.consent:
                    return new Consent(type, value, action, element)
                case AnalyticsService.clickEventTypes.widget:
                    return new Widget(type, value, action, element)
                case AnalyticsService.clickEventTypes.navigation:
                    return new Navigation(type, value, action, element)
                case AnalyticsService.clickEventTypes.internalPromotionClick:
                    return new InternalPromo(type, value, action, element)
                case AnalyticsService.clickEventTypes.subscribeNewsletter:
                    return new SubscribeNewsletter(type, value, action, element)
                case AnalyticsService.clickEventTypes.socialShare:
                    return new SocialShare(type, value, action, element)
                case AnalyticsService.clickEventTypes.subscriptionButton:
                case AnalyticsService.clickEventTypes.signinRegModule:
                case AnalyticsService.clickEventTypes.signout:
                case AnalyticsService.clickEventTypes.accountModal:
                    return new Subscription(type, value, action, element)
                case AnalyticsService.clickEventTypes.photoGallery:
                    return new PhotoGallery(type, value, action, element)
                default:
                    return new Default(type, value, action, element)
            }
        } else if (action === AnalyticsService.ACTION_PAGE_LOAD) {
            switch (type.toLowerCase()) {
                case AnalyticsService.pageLoadEventTypes.viewPage:
                    return new ScreenView(type, value, action, element)
                case AnalyticsService.pageLoadEventTypes.viewArticle:
                    return new ArticleView(type, value, action, element)
                case AnalyticsService.pageLoadEventTypes.widgetVideoLoad:
                    return new WidgetVideo(type, value, action, element)
                case AnalyticsService.pageLoadEventTypes.viewCategory:
                    return new CategoryView(type, value, action, element)
                case AnalyticsService.pageLoadEventTypes.search:
                    return new Search(type, value, action, element)
                case AnalyticsService.pageLoadEventTypes.errorMessageDisplayed:
                    return new PageError(type, value, action, element)
                case AnalyticsService.pageLoadEventTypes.campaign:
                    return new Campaign(type, value, action, element)
                case AnalyticsService.pageLoadEventTypes.videoCenter:
                    return new VideoCenter(type, value, action, element)
                case AnalyticsService.pageLoadEventTypes.newReg:
                case AnalyticsService.pageLoadEventTypes.regThankScreen:
                case AnalyticsService.pageLoadEventTypes.verificationScreen:
                case AnalyticsService.pageLoadEventTypes.creditCardInfo:
                case AnalyticsService.pageLoadEventTypes.accountConfirmed:
                    return new Subscription(type, value, action, element)
                default:
                    return new Default(type, value, action, element)
            }
        } else if (action === DOMEvents.scroll) {
            switch (type.toLowerCase()) {
                case AnalyticsService.scrollEventTypes.percentPageview:
                    return new PageScroll(type, value, action, element)
                case AnalyticsService.scrollEventTypes.widgetInview:
                    return new WidgetInview(type, value, action, element)
            }
        }

        return new Default(type, value, action, element)
    }

    /**
     * Bind click events to it elements
     * page-load evets are added to this.events and passed
     * over pageOnLoadEvents
     */
    private bindEvents(events: IMparticleEvent[], meta: object): void {
        for (const event of events) {
            if (event instanceof MparticleEvent) {
                // if the event has been identified as an mparticle specific one, set the meta properties
                // - possibly not the cleanest way to do this
                event.setMetaProperties(meta)
            }

            // if the event is a page load event, fire it right away
            if (AnalyticsService.ACTION_PAGE_LOAD === event.action) {
                // handle the event
                event.handle()

            } else if (event.action === DOMEvents.click) {
                // add a click listener for the event
                event.element.addEventListener(DOMEvents.click, event.handle.bind(event))

            } else if (event.action === DOMEvents.scroll) {
                // add a scroll listener
                document.addEventListener(DOMEvents.scroll, event.handle.bind(event))

            } else {
                // @TODO: log error
            }
        }
    }

    /**
     * Bind video events to document
     */
    private bindVideoEvents(meta: object): void {
        const videoEvents = [
            'video-full-screen',
            'video-started',
            'video-paused',
            'video-ended',
            'video-player-error'
        ]

        videoEvents.forEach((item) => {
            document.addEventListener(
                item,
                (e: any) => {
                    const videoEvent = new VideoEvents(e.type, e.detail, '')
                    videoEvent.setMetaProperties(meta)
                    videoEvent.handle()
                },
                false
            )
        })
    }

    /**
     * Bind viafoura events to document
     */
    private bindViafouraEvents(meta: object): void {

        const viafouraEvents = [
            'comment-disliked',
            'comment-liked',
            'user-comment',
            'user-updated'
        ]

        viafouraEvents.forEach((item) => {
            document.addEventListener(
                item,
                (e: any) => {
                    const viafouraEvent = new ViafouraEvents(e.type, e.detail, '')
                    viafouraEvent.setMetaProperties(meta)
                    viafouraEvent.handle()
                },
                false
            )
        })
    }

    /**
     * Bind subscription events to document
     */
    private bindSubscriptionEvents(meta: object): void {

        const events = [
            'new-registration',
            'signin',
            'signout',
            'consent'
        ]

        events.forEach((item) => {
            document.addEventListener(
                item,
                (e: any) => {
                    const customSubscription = new CustomSubscription(e.type, e.detail, '')
                    customSubscription.setMetaProperties(meta)
                    customSubscription.handle()
                },
                false
            )
        })
    }
}
